import React from 'react';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { ReactComponent as StarIcon } from '@rocco/icons/svg/star.svg';
import { HomeCarouselSection } from '@rocco/ui/layout/components/HomeCarouselSection';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { Button as RoccoButton } from '@rocco/components/button';
import { useAtomValue } from 'jotai';
import { remixHomePageTrackingContext } from '~/ui-rocco/hooks/use-home-page-tracking';
import { Link } from 'react-router';

interface TestimonialSection {
    title: string;
    description: string;
    testimonials: Testimonial[];
}

interface Testimonial {
    quote: string;
    author: string;
    company: string;
    rating: number;
    image: string;
    link?: string;
}
export const HomePageTestimonials = ({
    testimonialSection,
    appDisplayMode,
    openLogin,
    overriteTracking,
}: {
    testimonialSection?: TestimonialSection;
    appDisplayMode: 'desktop' | 'mobile';
    openLogin: (url: string) => void;
    overriteTracking?: () => void;
}) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const { onStartProjectClick } = useAtomValue(remixHomePageTrackingContext);

    return (
        <section className="bg-[#F2F2F2] py-20 md:py-48">
            <HomeCarouselSection
                displayMode={appDisplayMode}
                title={testimonialSection?.title}
                description={testimonialSection?.description}
                onSlideChange={setActiveIndex}
            >
                {testimonialSection?.testimonials?.map((item, index) => (
                    <div
                        key={`testimonials-${index}`}
                        className={cn(
                            'flex aspect-280/350 flex-col rounded-2 p-4.5 backdrop-blur-[0.46875rem] transition-colors duration-300 md:aspect-600/690 md:h-[42.7rem] md:pt-[3.125rem] md:pr-[3.125rem] md:pb-12 md:pl-12',
                            activeIndex === index
                                ? 'bg-[#AC5B39]'
                                : 'bg-[#717677]',
                        )}
                    >
                        <div className="pb-[2.375rem] text-right md:pb-[3.2rem]">
                            <div className="relative ml-auto flex size-21 items-center justify-center overflow-hidden rounded-full bg-gray-300 md:size-58.25">
                                {item.image ? (
                                    <Image
                                        src={item.image}
                                        alt={item.author}
                                        fill={true}
                                        loading="lazy"
                                        fit="cover"
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className="flex flex-col gap-4 md:gap-12">
                            <span className="pb-[0.35rem] text-sm leading-[142.85%] md:w-121.75 md:text-7 md:leading-[121.43%]">
                                {item.quote}
                            </span>
                            <div className="flex">
                                {[...Array(item.rating)].map((_, index) => (
                                    <StarIcon
                                        key={index}
                                        className="size-4 md:size-6"
                                    />
                                ))}
                            </div>
                            <div className="mt-4 md:mt-auto">
                                <RoccoButton
                                    color={
                                        activeIndex === index
                                            ? 'glassy'
                                            : 'gray'
                                    }
                                    inverted={activeIndex === index}
                                    onClick={() => {
                                        if (overriteTracking) {
                                            overriteTracking();
                                        } else {
                                            onStartProjectClick?.(
                                                'HomePageTestimonials',
                                            );
                                        }
                                    }}
                                    asChild
                                >
                                    <Link
                                        to={item.link ?? ''}
                                        onClick={(e) => {
                                            if (item.link) {
                                                e.preventDefault();
                                                openLogin(item.link);
                                            }
                                        }}
                                    >
                                        {`Start your Project`}
                                        <ArrowRight className="ml-1.5 w-4" />
                                    </Link>
                                </RoccoButton>
                            </div>
                        </div>
                    </div>
                ))}
            </HomeCarouselSection>
        </section>
    );
};
